/**
 * ActionWheelV2 — polished radial alternative to the original ActionWheel.
 *
 * Same radial pattern as the current wheel, but with production-quality
 * visual design: bigger hit targets, real labels under each icon (not
 * floating emojis), cleaner typography, smart clamping, smoother animation.
 * Drawing bugs are fixed at the PlaybookStudio surface level, not here.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// hasSvgIcon dropped — ActionIcon now handles custom SVG (from IDB via
// setCurrentDrawAction / newPass save flows in PlaybookStudio) + emoji fallback internally.
import { ActionIcon } from './ActionIcons';

const RING_RADIUS = 130;  // wider spread so items don't overlap or crowd
const ITEM_SIZE = 60;     // outer diameter of each item pill
const ICON_SIZE = 26;
const LABEL_HEIGHT = 16;
const CARD_PADDING = 24;

// Backdrop stays transparent — Dave wants to see the play behind the wheel.
// The wheel's individual button wells have their own foggy glass styling.
// zIndex bumped above mobileCollapsedPanel (100). See docs/plans/mobile-drag-and-zindex.md Bug 2.
const BACKDROP_STYLE = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 200,
  cursor: 'default',
};

const WHEEL_ROOT_BASE = {
  position: 'absolute',
  transform: 'translate(-50%, -50%)',
  zIndex: 201,
  pointerEvents: 'none',
};

const CENTER_STYLE = (accent) => ({
  position: 'absolute',
  top: '50%', left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 14, height: 14,
  borderRadius: '50%',
  background: `radial-gradient(circle at 30% 30%, ${accent}, ${accent}aa)`,
  boxShadow: `0 0 16px ${accent}66`,
  pointerEvents: 'none',
  zIndex: 3,
});

const CENTER_LABEL_STYLE = (accent) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -180%)',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 3.5,
  textTransform: 'uppercase',
  background: `linear-gradient(180deg, #fff, ${accent})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  filter: `drop-shadow(0 1px 6px ${accent}88)`,
});

const itemStyle = (offsetX, offsetY, hovered, accent) => ({
  position: 'absolute',
  top: `calc(50% + ${offsetY}px)`,
  left: `calc(50% + ${offsetX}px)`,
  transform: `translate(-50%, -50%) scale(${hovered ? 1.08 : 1})`,
  width: ITEM_SIZE,
  height: ITEM_SIZE + LABEL_HEIGHT,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  pointerEvents: 'auto',
  cursor: 'pointer',
  transition: 'transform 0.14s cubic-bezier(0.34, 1.56, 0.64, 1)',
  fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif",
});

// Glass morphism icon well — semi-opaque, blurred, subtle inner highlight,
// crisp 1px border. Hover lights up with the sport accent as a border ring.
const iconWrapStyle = (hovered, accent) => ({
  width: ITEM_SIZE,
  height: ITEM_SIZE,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Higher opacity than before so buttons stand out against the visible
  // court/pitch beneath (backdrop is transparent per Dave's ask).
  background: hovered
    ? `linear-gradient(160deg, ${accent}44 0%, rgba(10,14,22,0.86) 60%, rgba(6,9,15,0.9) 100%)`
    : 'linear-gradient(160deg, rgba(20,24,35,0.85) 0%, rgba(10,14,22,0.88) 60%, rgba(6,9,15,0.9) 100%)',
  border: hovered
    ? `1px solid ${accent}`
    : '1px solid rgba(255,255,255,0.16)',
  boxShadow: hovered
    ? `0 10px 30px ${accent}55, 0 0 0 4px ${accent}18, inset 0 1px 0 rgba(255,255,255,0.2), inset 0 -2px 4px rgba(0,0,0,0.35)`
    : '0 6px 18px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.14), inset 0 -1px 2px rgba(0,0,0,0.35)',
  color: hovered ? accent : 'rgba(255,255,255,0.92)',
  transition: 'background 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease, color 0.16s ease',
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
});

// Labels sit in a dark glass pill so they're readable against any court color
// (hardwood, white lines, grass, dirt) — the pill provides consistent contrast.
const labelStyle = (hovered, accent) => ({
  marginTop: 8,
  fontSize: 10,
  fontWeight: 700,
  color: hovered ? accent : '#ffffff',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  letterSpacing: 1.4,
  textTransform: 'uppercase',
  padding: '3px 9px',
  borderRadius: 999,
  background: hovered
    ? 'linear-gradient(180deg, rgba(9,10,15,0.9), rgba(6,9,15,0.94))'
    : 'linear-gradient(180deg, rgba(9,10,15,0.82), rgba(6,9,15,0.88))',
  border: `1px solid ${hovered ? accent + '88' : 'rgba(255,255,255,0.14)'}`,
  backdropFilter: 'blur(10px) saturate(160%)',
  WebkitBackdropFilter: 'blur(10px) saturate(160%)',
  boxShadow: '0 4px 12px rgba(0,0,0,0.45)',
  transition: 'color 0.16s ease, background 0.16s ease, border-color 0.16s ease',
});

const ActionWheelV2 = ({ x, y, actions, onSelect, onDismiss, accentColor, sportId }) => {
  const rootRef = useRef(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ left: `${x}%`, top: `${y}%` });
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 640
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Mobile-tuned sizing so the wheel fits on phone screens without clipping.
  const ringRadius = isMobile ? 90 : RING_RADIUS;
  const itemSize = isMobile ? 52 : ITEM_SIZE;
  const iconSize = isMobile ? 22 : ICON_SIZE;

  const accent = accentColor || '#22d3ee';
  const PAGE_SIZE = 8;
  const [pageIdx, setPageIdx] = useState(0);
  const totalPages = Math.max(1, Math.ceil(actions.length / PAGE_SIZE));
  // Clamp pageIdx if actions shrink between opens (e.g. player swap)
  useEffect(() => { if (pageIdx >= totalPages) setPageIdx(0); }, [pageIdx, totalPages]);
  const displayActions = useMemo(
    () => actions.slice(pageIdx * PAGE_SIZE, pageIdx * PAGE_SIZE + PAGE_SIZE),
    [actions, pageIdx]
  );
  const count = displayActions.length;

  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onDismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) onDismiss();
  }, [onDismiss]);

  // Clamp to viewport / parent so the wheel doesn't spill off-screen.
  useEffect(() => {
    if (!rootRef.current) return;
    const el = rootRef.current;
    const parent = el.offsetParent;
    if (!parent) return;
    const parentRect = parent.getBoundingClientRect();
    const R = RING_RADIUS + ITEM_SIZE / 2 + CARD_PADDING;

    let cx = (x / 100) * parentRect.width;
    let cy = (y / 100) * parentRect.height;

    if (cx - R < 0) cx = R;
    if (cx + R > parentRect.width) cx = parentRect.width - R;
    if (cy - R < 0) cy = R;
    if (cy + R > parentRect.height) cy = parentRect.height - R;

    setPos({ left: `${cx}px`, top: `${cy}px` });
  }, [x, y, count]);

  const hoveredAction = hoveredIndex != null ? displayActions[hoveredIndex] : null;

  return (
    <div
      style={BACKDROP_STYLE}
      onClick={handleBackdropClick}
    >
      <div
        ref={rootRef}
        style={{
          ...WHEEL_ROOT_BASE,
          ...pos,
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.14s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Center anchor */}
        <div style={CENTER_STYLE(accent)} />

        {/* Center label — shows the hovered action name in gradient text */}
        {hoveredAction && (
          <div style={CENTER_LABEL_STYLE(accent)}>{hoveredAction.name || hoveredAction.id}</div>
        )}

        {/* Pagination — only shown when the action list overflows one ring.
            Sits below the center dot so it doesn't steal focus from actions. */}
        {totalPages > 1 && (
          <div style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, 40%)',
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '3px 8px', borderRadius: 999,
            background: 'linear-gradient(180deg, rgba(9,10,15,0.9), rgba(6,9,15,0.94))',
            border: `1px solid ${accent}66`,
            backdropFilter: 'blur(10px) saturate(160%)',
            pointerEvents: 'auto', zIndex: 4,
            fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif",
          }}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPageIdx((i) => (i - 1 + totalPages) % totalPages); }}
              aria-label="Previous actions"
              style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 14, padding: '0 4px' }}
            >‹</button>
            <span style={{ color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: 1 }}>
              {pageIdx + 1}/{totalPages}
            </span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPageIdx((i) => (i + 1) % totalPages); }}
              aria-label="Next actions"
              style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 14, padding: '0 4px' }}
            >›</button>
          </div>
        )}

        {/* Radial items */}
        {displayActions.map((action, i) => {
          const angle = (i / count) * Math.PI * 2 - Math.PI / 2;
          const dx = Math.cos(angle) * RING_RADIUS;
          const dy = Math.sin(angle) * RING_RADIUS;
          const hovered = hoveredIndex === i;
          const scale = visible ? 1 : 0;
          return (
            <button
              key={action.id}
              type="button"
              style={{
                ...itemStyle(dx, dy, hovered, accent),
                transform: `translate(-50%, -50%) scale(${scale * (hovered ? 1.08 : 1)})`,
                transition: `transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1) ${i * 20}ms, opacity 0.14s`,
                background: 'transparent',
                border: 'none',
                padding: 0,
              }}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={() => onSelect(action)}
              data-action-id={action.id}
              aria-label={action.name || action.id}
            >
              <div style={iconWrapStyle(hovered, accent)}>
                {/* ActionIcon: custom SVG (IDB) > bundled > emoji fallback */}
                <ActionIcon
                  actionId={action.id}
                  size={ICON_SIZE}
                  sportId={sportId}
                  emojiFallback={action.icon}
                />
              </div>
              <div style={labelStyle(hovered, accent)}>{action.name || action.id}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ActionWheelV2;
